import * as stylex from '@stylexjs/stylex'
import { useEffect, useMemo, useState } from "react";

import Dialog from "./Dialog";
import Avatar from "./Avatar";
import Icon from "./Icon";
import {
  equalShares,
  findGroup,
  findPerson,
  parseCents,
  currencyOptions,
  type CreateExpenseInput,
  type Overview,
} from "../lib/ledger";


const styles = stylex.create({
  s0: {
    display: "flex",
    flexDirection: "column",
    rowGap: "24px",
  },
  s1: {
    display: "grid",
    gap: "16px",
    "@media (min-width: 640px)": {
      gridTemplateColumns: "minmax(0,1fr) 220px",
    },
  },
  s2: {
    display: "block",
  },
  s3: {
    marginBottom: "8px",
    display: "block",
    fontSize: "13px",
    fontWeight: 600,
    color: "#3d463f",
  },
  s4: {
    position: "relative",
  },
  s5: {
    height: "48px",
    width: "100%",
    borderRadius: "0.75rem",
    borderWidth: 1,
    borderColor: "#dbe2d9",
    backgroundColor: "#fff",
    paddingInline: "14px",
    fontSize: "14px",
    outline: "none",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    "::placeholder": {
      color: "#99a099",
    },
    ":focus": {
      borderColor: "#5b9a6c",
      boxShadow: "0 0 0 1px color-mix(in srgb, #5b9a6c 10%, transparent)",
    },
  },
  s6: {
    position: "absolute",
    zIndex: "30",
    marginTop: "8px",
    maxHeight: "224px",
    width: "100%",
    overflowY: "auto",
    borderRadius: "0.75rem",
    borderWidth: 1,
    borderColor: "#dbe2d9",
    backgroundColor: "#fff",
    padding: "6px",
    boxShadow: "0 10px 24px rgba(48,70,51,.14)",
  },
  s7: {
    display: "flex",
    height: "48px",
    overflow: "hidden",
    borderRadius: "0.75rem",
    borderWidth: 1,
    borderColor: "#dbe2d9",
    backgroundColor: "#fff",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    ":focus-within": {
      borderColor: "#5b9a6c",
      boxShadow: "0 0 0 1px color-mix(in srgb, #5b9a6c 10%, transparent)",
    },
  },
  s8: {
    minWidth: "0px",
    flex: "1 1 0%",
    paddingInline: "14px",
    fontSize: "15px",
    fontWeight: 600,
    outline: "none",
    "::placeholder": {
      fontWeight: 400,
      color: "#99a099",
    },
  },
  s9: {
    borderLeftWidth: 1,
    borderColor: "#dbe2d9",
    backgroundColor: "#f9fbf8",
    paddingInline: "10px",
    fontSize: "11px",
    fontWeight: 600,
    color: "#68806d",
    outline: "none",
  },
  s13: {
    marginBottom: "8px",
    fontSize: "13px",
    fontWeight: 600,
    color: "#3d463f",
  },
  s14: {
    marginLeft: "12px",
  },
  s15: {
    height: "100%",
    minWidth: "0px",
    flex: "1 1 0%",
    backgroundColor: "transparent",
    paddingInline: "12px",
    fontSize: "13px",
    outline: "none",
    "::placeholder": {
      color: "#99a099",
    },
  },
  s16: {
    paddingInline: "12px",
    color: "#748077",
  },
  s17: {
    paddingInline: "12px",
    paddingBlock: "8px",
    fontSize: "12px",
    color: "#748077",
  },
  s18: {
    minWidth: "0px",
    flex: "1 1 0%",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
  s19: {
    height: "16px",
    width: "16px",
    color: "#4d9460",
  },
  s20: {
    height: "44px",
    width: "100%",
    borderRadius: "0.75rem",
    borderWidth: 1,
    borderColor: "#dbe2d9",
    paddingInline: "12px",
    paddingRight: "40px",
    fontSize: "13px",
  },
  s21: {
    position: "absolute",
    right: "8px",
    top: "8px",
    display: "flex",
    height: "28px",
    width: "28px",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "0.5rem",
    fontSize: "18px",
    lineHeight: 1,
    color: "#7b887d",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    ":hover": {
      backgroundColor: "#edf5eb",
      color: "#3e7d4d",
    },
  },
  s22: {
    position: "absolute",
    zIndex: "30",
    marginTop: "8px",
    maxHeight: "224px",
    width: "100%",
    overflowY: "auto",
    borderRadius: "0.75rem",
    borderWidth: 1,
    borderColor: "#dbe2d9",
    backgroundColor: "#fff",
    padding: "6px",
    boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
  },
  s23: {
    flex: "1 1 0%",
  },
  s24: {
    marginTop: "8px",
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
  },
  s25: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    borderRadius: "0.5rem",
    backgroundColor: "#edf7eb",
    paddingInline: "8px",
    paddingBlock: "4px",
    fontSize: "11px",
    fontWeight: 600,
    color: "#3e7d4d",
  },
  s26: {
    display: "flex",
    height: "16px",
    width: "16px",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "9999px",
    fontSize: "14px",
    lineHeight: 1,
    color: "#5d8064",
    ":hover": {
      backgroundColor: "#d8ecd5",
    },
  },
  s27: {
    marginTop: "8px",
    fontSize: "11px",
    color: "#768077",
  },
  s28: {
    maxHeight: "320px",
    overflowY: "auto",
    paddingRight: "4px",
  },
  s29: {
    display: "grid",
    gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
    gap: "4px",
    borderRadius: "0.75rem",
    backgroundColor: "#f2f6f1",
    padding: "4px",
  },
  s30: {
    marginTop: "12px",
    display: "flex",
    flexDirection: "column",
    rowGap: "8px",
  },
  s31: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  s32: {
    height: "36px",
    minWidth: "0px",
    flex: "1 1 0%",
    borderRadius: "0.5rem",
    borderWidth: 1,
    borderColor: "#dbe2d9",
    paddingInline: "10px",
    fontSize: "12px",
  },
  s33: {
    height: "36px",
    width: "96px",
    borderRadius: "0.5rem",
    borderWidth: 1,
    borderColor: "#dbe2d9",
    paddingInline: "10px",
    textAlign: "right",
    fontSize: "12px",
  },
  s34: {
    height: "32px",
    width: "32px",
    borderRadius: "0.5rem",
    fontSize: "1.125rem",
    color: "#7b887d",
    ":hover": {
      backgroundColor: "#edf5eb",
    },
  },
  s35: {
    fontSize: "12px",
    fontWeight: 600,
    color: "#3e7d4d",
  },
  s36: {
    minWidth: "0px",
    flex: "1 1 0%",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    fontSize: "12px",
    color: "#526057",
  },
  s37: {
    display: "flex",
    height: "36px",
    width: "144px",
    alignItems: "center",
    overflow: "hidden",
    borderRadius: "0.5rem",
    borderWidth: 1,
    borderColor: "#dbe2d9",
    backgroundColor: "#fff",
  },
  s38: {
    minWidth: "0px",
    flex: "1 1 0%",
    paddingInline: "10px",
    textAlign: "right",
    fontSize: "13px",
    outline: "none",
  },
  s39: {
    paddingInline: "8px",
    fontSize: "11px",
    color: "#7a887d",
  },
  s40: {
    borderRadius: "0.75rem",
    borderWidth: 1,
    borderColor: "#f0d0c0",
    backgroundColor: "#fff5f0",
    paddingInline: "14px",
    paddingBlock: "12px",
    fontSize: "13px",
    color: "#9b491e",
  },
  s41: {
    display: "flex",
    flexDirection: "column-reverse",
    gap: "12px",
    borderTopWidth: 1,
    borderColor: "#e8ece7",
    paddingTop: "20px",
    "@media (min-width: 640px)": {
      flexDirection: "row",
      justifyContent: "end",
    },
  },
  s42: {
    height: "44px",
    borderRadius: "0.75rem",
    paddingInline: "16px",
    fontSize: "13px",
    fontWeight: 600,
    color: "#607068",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    ":hover": {
      backgroundColor: "#f0f4ef",
    },
  },
  s43: {
    display: "flex",
    height: "44px",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    borderRadius: "0.75rem",
    backgroundColor: "#408b56",
    paddingInline: "20px",
    fontSize: "13px",
    fontWeight: 600,
    color: "#fff",
    boxShadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    ":hover": {
      backgroundColor: "#347c48",
    },
    ":disabled": {
      cursor: "not-allowed",
      opacity: 0.6,
    },
  },
  s44: {
    height: "16px",
    width: "16px",
  },
  s45: {
    height: "48px",
    width: "100%",
    borderRadius: "0.75rem",
    borderWidth: 1,
    borderColor: "#dbe2d9",
    backgroundColor: "#fff",
    paddingInline: "14px",
    fontSize: "14px",
    outline: "none",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    ":focus": {
      borderColor: "#5b9a6c",
      boxShadow: "0 0 0 1px color-mix(in srgb, #5b9a6c 10%, transparent)",
    },
  },
})

const expenseDialogStyles = stylex.create({
  option: { display: 'flex', width: '100%', alignItems: 'center', gap: '8px', borderRadius: '0.5rem', paddingInline: '12px', paddingBlock: '8px', textAlign: 'left', fontSize: '13px', transition: 'color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter' },
  optionSelected: { backgroundColor: '#edf7eb', fontWeight: 600, color: '#3e7d4d' },
  optionIdle: { color: '#46544a', ':hover': { backgroundColor: '#f5faf3' } },
  payer: { display: 'flex', height: '48px', alignItems: 'center', borderRadius: '0.75rem', borderWidth: 1, backgroundColor: '#fff', transition: 'color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter' },
  payerOpen: { borderColor: '#5b9a6c', boxShadow: '0 0 0 4px color-mix(in srgb, #5b9a6c 10%, transparent)' },
  payerClosed: { borderColor: '#e0e6de' },
  participantIdle: { ':hover': { backgroundColor: '#edf7eb' } },
  split: { borderRadius: '0.5rem', paddingInline: '8px', paddingBlock: '8px', fontSize: '12px', fontWeight: 600, transition: 'color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter' },
  splitSelected: { backgroundColor: '#fff', color: '#3e7d4d', boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)' },
  splitIdle: { color: '#718073', ':hover': { color: '#3e7d4d' } },
})
type SplitType = "equal" | "exact" | "percentage" | "shares" | "items";
type ReceiptItem = { id: number; name: string; amount: string };

function dateInputValue(value: Date) {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

type ExpenseDialogProps = {
  data: Overview;
  defaultGroupID?: string;
  defaultPersonID?: string;
  onClose: () => void;
  onSave: (input: CreateExpenseInput) => Promise<void>;
};

export default function ExpenseDialog({
  data,
  defaultGroupID,
  defaultPersonID,
  onClose,
  onSave,
}: ExpenseDialogProps) {
  const initialGroupID = defaultGroupID ?? "";
  const [description, setDescription] = useState("");
  const [descriptionOpen, setDescriptionOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [expenseDate, setExpenseDate] = useState(() => dateInputValue(new Date()));
  const [currency, setCurrency] = useState(data.currentUser.currency);
  const [groupID] = useState(initialGroupID);
  const [paidByID, setPaidByID] = useState(data.currentUser.id);
  const [payerOpen, setPayerOpen] = useState(false);
  const [payerQuery, setPayerQuery] = useState("");
  const [participantIDs, setParticipantIDs] = useState<string[]>(
    Array.from(
      new Set(
        [data.currentUser.id, defaultPersonID].filter(Boolean) as string[],
      ),
    ),
  );
  const [splitOpen, setSplitOpen] = useState(false);
  const [splitQuery, setSplitQuery] = useState("");
  const [splitType, setSplitType] = useState<SplitType>("equal");
  const [splitValues, setSplitValues] = useState<Record<string, string>>({});
  const [items, setItems] = useState<ReceiptItem[]>([
    { id: 1, name: "", amount: "" },
  ]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    const closeDropdowns = (event: Event) => {
      const target = event.target;
      if (!(target instanceof Element) || !target.closest("[data-dropdown]")) {
        setDescriptionOpen(false);
        setPayerOpen(false);
        setSplitOpen(false);
      }
    };
    document.addEventListener("pointerdown", closeDropdowns);
    document.addEventListener("click", closeDropdowns);
    document.addEventListener("focusin", closeDropdowns);
    return () => {
      document.removeEventListener("pointerdown", closeDropdowns);
      document.removeEventListener("click", closeDropdowns);
      document.removeEventListener("focusin", closeDropdowns);
    };
  }, []);

  const group = findGroup(data, groupID);
  const members = group?.memberIds ?? [
    data.currentUser.id,
    ...data.people.map((person) => person.id),
  ];
  const filteredMembers = members.filter((memberID) => {
    const person = findPerson(data, memberID);
    return (memberID === data.currentUser.id ? "you" : (person?.name ?? ""))
      .toLowerCase()
      .includes(payerQuery.toLowerCase());
  });
  const selectedPayer = findPerson(data, paidByID);
  const filteredSplitMembers = members
    .filter((memberID) => memberID !== paidByID)
    .filter((memberID) => {
      const person = findPerson(data, memberID);
      return (memberID === data.currentUser.id ? "you" : (person?.name ?? ""))
        .toLowerCase()
        .includes(splitQuery.toLowerCase());
    });
  const descriptionSuggestions = Array.from(
    new Set(
      data.expenses
        .map((expense) => expense.description.trim())
        .filter(Boolean),
    ),
  )
    .filter((value) => value.toLowerCase().includes(description.toLowerCase()))
    .sort((left, right) => left.localeCompare(right))
    .slice(0, 8);
  const amountCents = parseCents(amount);
  const shares = useMemo(
    () => equalShares(amountCents ?? 0, participantIDs.length),
    [amountCents, participantIDs.length],
  );
  const splitRecords = useMemo(
    () =>
      participantIDs.map((personID, index) => {
        const value = Number((splitValues[personID] ?? "").replace(",", "."));
        if (splitType === "equal" || splitType === "items")
          return { personId: personID, amountCents: shares[index] ?? 0 };
        if (splitType === "percentage")
          return {
            personId: personID,
            percentage: Number.isFinite(value) ? Math.round(value * 100) : 0,
          };
        if (splitType === "shares")
          return {
            personId: personID,
            shares: Number.isFinite(value) ? Math.round(value) : 0,
          };
        return {
          personId: personID,
          amountCents: parseCents(splitValues[personID] ?? "") ?? 0,
        };
      }),
    [participantIDs, shares, splitType, splitValues],
  );
  const splitTotal =
    splitType === "percentage"
      ? splitRecords.reduce(
          (total, split) => total + (split.percentage ?? 0),
          0,
        )
      : splitType === "shares"
        ? splitRecords.reduce((total, split) => total + (split.shares ?? 0), 0)
        : splitRecords.reduce(
            (total, split) => total + (split.amountCents ?? 0),
            0,
          );
  const itemTotal = items.reduce(
    (total, item) => total + (parseCents(item.amount) ?? 0),
    0,
  );
  const splitValid =
    participantIDs.length >= 2 &&
    (splitType === "items"
      ? itemTotal === amountCents &&
        items.every(
          (item) =>
            item.name.trim() &&
            parseCents(item.amount) !== null &&
            (parseCents(item.amount) ?? 0) > 0,
        )
      : splitType === "percentage"
        ? splitTotal === 10000
        : splitType === "shares"
          ? splitTotal > 0 &&
            splitRecords.every((split) => (split.shares ?? 0) > 0)
          : splitTotal === amountCents);
  const isValid =
    Boolean(description.trim()) &&
    amountCents !== null &&
    amountCents > 0 &&
    splitValid;

  function toggleParticipant(id: string) {
    if (id === paidByID) return;
    setParticipantIDs((current) =>
      current.includes(id)
        ? current.filter((memberID) => memberID !== id)
        : [...current, id],
    );
  }

  function handleCentsKeyDown(
    event: React.KeyboardEvent<HTMLInputElement>,
    current: string,
    setValue: (value: string) => void,
  ) {
    if (/^\d$/.test(event.key)) {
      event.preventDefault();
      const cents = (parseCents(current) ?? 0) * 10 + Number(event.key);
      setValue((cents / 100).toFixed(2));
    } else if (event.key === "Backspace") {
      event.preventDefault();
      const cents = Math.floor((parseCents(current) ?? 0) / 10);
      setValue(cents ? (cents / 100).toFixed(2) : "");
    } else if (event.key === "Delete") {
      event.preventDefault();
      setValue("");
    } else if (
      !["Tab", "ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)
    ) {
      event.preventDefault();
    }
  }

  function updateSplitValue(personID: string, nextValue: string) {
    setSplitValues((current) => {
      const next = { ...current, [personID]: nextValue };
      if (splitType === "exact") {
        const total = participantIDs.reduce(
          (sum, id) => sum + (parseCents(next[id] ?? "") ?? 0),
          0,
        );
        setAmount((total / 100).toFixed(2));
      }
      return next;
    });
  }

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!isValid || amountCents === null) {
      setError("Add a description, a valid amount, and at least two people.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await onSave({
        description: description.trim(),
        amountCents,
        currency,
        occurredAt: `${expenseDate}T12:00:00.000Z`,
        groupId: groupID || undefined,
        paidById: paidByID,
        participantIds: participantIDs,
        splitType,
        splits: splitRecords,
      });
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "We could not save that expense.",
      );
      setSaving(false);
    }
  }

  return (
    <Dialog
      description="Keep the details clear now so everyone knows where they stand."
      onClose={saving ? () => undefined : onClose}
      title="Add an expense"
    >
      <form {...stylex.props(styles.s0)} onSubmit={submit}>
        <div {...stylex.props(styles.s1)}>
          <label {...stylex.props(styles.s2)}>
            <span {...stylex.props(styles.s3)}>
              What was it for?
            </span>
            <div {...stylex.props(styles.s4)} data-dropdown onBlur={(event) => { if (!event.relatedTarget || !event.currentTarget.contains(event.relatedTarget)) setDescriptionOpen(false) }}>
              <input
                autoFocus
                aria-controls="expense-description-options"
                aria-expanded={descriptionOpen}
                {...stylex.props(styles.s5)}
                data-dialog-autofocus
                maxLength={120}
                onChange={(event) => {
                  setDescription(event.target.value);
                  setDescriptionOpen(true);
                }}
                onFocus={() => setDescriptionOpen(true)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && descriptionSuggestions[0]) {
                    event.preventDefault();
                    setDescription(descriptionSuggestions[0]);
                    setDescriptionOpen(false);
                  }
                }}
                placeholder="Search or enter a restaurant, merchant, or expense…"
                value={description}
              />
              {descriptionOpen && descriptionSuggestions.length > 0 && (
                <div
                  {...stylex.props(styles.s6)}
                  id="expense-description-options"
                  role="listbox"
                >
                  {descriptionSuggestions.map((value, index) => (
                    <button
                      aria-selected={description === value}
                      {...stylex.props(expenseDialogStyles.option, index === 0 ? expenseDialogStyles.optionSelected : expenseDialogStyles.optionIdle)}
                      key={value}
                      onClick={() => {
                        setDescription(value);
                        setDescriptionOpen(false);
                      }}
                      role="option"
                      type="button"
                    >
                      {value}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </label>
          <label {...stylex.props(styles.s2)}>
            <span {...stylex.props(styles.s3)}>
              Amount
            </span>
            <div {...stylex.props(styles.s7)}>
              <input
                {...stylex.props(styles.s8)}
                inputMode="numeric"
                type="text"
                onKeyDown={(event) =>
                  handleCentsKeyDown(event, amount, setAmount)
                }
                onChange={() => undefined}
                placeholder="0.00"
                value={amount}
              />
              <select
                aria-label="Expense currency"
                {...stylex.props(styles.s9)}
                onChange={(event) => setCurrency(event.target.value)}
                value={currency}
              >
                {currencyOptions(data.currentUser.currency).map((code) => (
                  <option key={code} value={code}>
                    {code}
                  </option>
                ))}
              </select>
            </div>
          </label>
        </div>

        <label {...stylex.props(styles.s2)}>
          <span {...stylex.props(styles.s3)}>
            Date
          </span>
          <input
            aria-label="Expense date"
            {...stylex.props(styles.s45)}
            onChange={(event) => setExpenseDate(event.target.value)}
            required
            type="date"
            value={expenseDate}
          />
        </label>

        <>
          <fieldset>
            <legend {...stylex.props(styles.s13)}>
              Paid by
            </legend>
            <div {...stylex.props(styles.s4)} data-dropdown onBlur={(event) => { if (!event.relatedTarget || !event.currentTarget.contains(event.relatedTarget)) setPayerOpen(false) }}>
              <div
                {...stylex.props(expenseDialogStyles.payer, payerOpen ? expenseDialogStyles.payerOpen : expenseDialogStyles.payerClosed)}
              >
                <Avatar
                  {...stylex.props(styles.s14)}
                  person={
                    selectedPayer ?? { id: paidByID, name: "Someone", color: 1 }
                  }
                  size={31}
                />
                <input
                  aria-controls="payer-options"
                  aria-expanded={payerOpen}
                  aria-label="Search who paid"
                  autoComplete="off"
                  {...stylex.props(styles.s15)}
                  onChange={(event) => {
                    setPayerQuery(event.target.value);
                    setPayerOpen(true);
                  }}
                  onFocus={() => setPayerOpen(true)}
                  placeholder={
                    paidByID === data.currentUser.id
                      ? "You"
                      : selectedPayer?.name
                  }
                  value={payerQuery}
                />
                <button
                  aria-label="Toggle payer options"
                  {...stylex.props(styles.s16)}
                  onClick={() => setPayerOpen((open) => !open)}
                  type="button"
                >
                  ⌄
                </button>
              </div>
              {payerOpen && (
                <div
                  {...stylex.props(styles.s6)}
                  id="payer-options"
                  role="listbox"
                >
                  {filteredMembers.length === 0 ? (
                    <p {...stylex.props(styles.s17)}>
                      No people found.
                    </p>
                  ) : (
                    filteredMembers.map((memberID) => {
                      const person = findPerson(data, memberID);
                      if (!person) return null;
                      const selected = paidByID === memberID;
                      return (
                        <button
                          aria-selected={selected}
                          {...stylex.props(expenseDialogStyles.option, selected ? expenseDialogStyles.optionSelected : expenseDialogStyles.optionIdle)}
                          key={memberID}
                          onClick={() => {
                            setPaidByID(memberID);
                            setParticipantIDs((current) =>
                              current.includes(memberID)
                                ? current
                                : [...current, memberID],
                            );
                            setPayerQuery("");
                            setPayerOpen(false);
                          }}
                          role="option"
                          type="button"
                        >
                          <Avatar person={person} size={29} />
                          <span {...stylex.props(styles.s18)}>
                            {memberID === data.currentUser.id
                              ? "You"
                              : person.name}
                          </span>
                          {selected && (
                            <Icon
                              {...stylex.props(styles.s19)}
                              name="check"
                            />
                          )}
                        </button>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          </fieldset>

          <fieldset>
            <legend {...stylex.props(styles.s13)}>
              Split with
            </legend>
            <div {...stylex.props(styles.s4)} data-dropdown onBlur={(event) => { if (!event.relatedTarget || !event.currentTarget.contains(event.relatedTarget)) setSplitOpen(false) }}>
              <input
                aria-label="Search people to split with"
                {...stylex.props(styles.s20)}
                onChange={(event) => {
                  setSplitQuery(event.target.value);
                  setSplitOpen(true);
                }}
                onFocus={() => setSplitOpen(true)}
                placeholder={
                  participantIDs.length
                    ? `${participantIDs.length} people selected`
                    : "Search and select people"
                }
                value={splitQuery}
              />
              {splitQuery && (
                <button
                  aria-label="Clear split search"
                  {...stylex.props(styles.s21)}
                  onClick={() => {
                    setSplitQuery("");
                    setSplitOpen(true);
                  }}
                  type="button"
                >
                  ×
                </button>
              )}
              {splitOpen && (
                <div
                  {...stylex.props(styles.s22)}
                  role="listbox"
                >
                  {filteredSplitMembers.length === 0 ? (
                    <p {...stylex.props(styles.s17)}>
                      No other people found.
                    </p>
                  ) : (
                    filteredSplitMembers.map((memberID) => (
                      <button
                        aria-selected={participantIDs.includes(memberID)}
                        {...stylex.props(expenseDialogStyles.option, participantIDs.includes(memberID) ? expenseDialogStyles.optionSelected : expenseDialogStyles.participantIdle)}
                        key={memberID}
                        onClick={() => toggleParticipant(memberID)}
                        role="option"
                        type="button"
                      >
                        <Avatar
                          person={findPerson(data, memberID)!}
                          size={28}
                        />
                        <span {...stylex.props(styles.s23)}>
                          {memberID === data.currentUser.id
                            ? "You"
                            : findPerson(data, memberID)?.name}
                        </span>
                        {participantIDs.includes(memberID) && (
                          <Icon
                            {...stylex.props(styles.s19)}
                            name="check"
                          />
                        )}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
            <div {...stylex.props(styles.s24)}>
              {participantIDs.map((id) => (
                <span
                  {...stylex.props(styles.s25)}
                  key={id}
                >
                  {id === data.currentUser.id
                    ? "You"
                    : findPerson(data, id)?.name}
                  {id !== paidByID && (
                    <button
                      aria-label="Remove selected person"
                      {...stylex.props(styles.s26)}
                      onClick={() => toggleParticipant(id)}
                      type="button"
                    >
                      ×
                    </button>
                  )}
                </span>
              ))}
            </div>
            <p {...stylex.props(styles.s27)}>
              Select at least two people to split this expense.
            </p>
          </fieldset>
          <fieldset {...stylex.props(styles.s28)}>
            <legend {...stylex.props(styles.s13)}>
              Split method
            </legend>
            <div {...stylex.props(styles.s29)}>
              {(
                [
                  ["equal", "Equal"],
                  ["exact", "Amounts"],
                  ["percentage", "%"],
                  ["shares", "Shares"],
                  ["items", "Items"],
                ] as const
              ).map(([value, label]) => (
                <button
                  {...stylex.props(expenseDialogStyles.split, splitType === value ? expenseDialogStyles.splitSelected : expenseDialogStyles.splitIdle)}
                  key={value}
                  onClick={() => setSplitType(value)}
                  type="button"
                >
                  {label}
                </button>
              ))}
            </div>
            {splitType === "items" ? (
              <div {...stylex.props(styles.s30)}>
                {items.map((item) => (
                  <div {...stylex.props(styles.s31)} key={item.id}>
                    <input
                      aria-label="Item name"
                      {...stylex.props(styles.s32)}
                      onChange={(event) =>
                        setItems((current) =>
                          current.map((entry) =>
                            entry.id === item.id
                              ? { ...entry, name: event.target.value }
                              : entry,
                          ),
                        )
                      }
                      placeholder="Item name"
                      value={item.name}
                    />
                    <input
                      aria-label="Item amount"
                      {...stylex.props(styles.s33)}
                      inputMode="numeric"
                      onKeyDown={(event) =>
                        handleCentsKeyDown(event, item.amount, (value) =>
                          setItems((current) =>
                            current.map((entry) =>
                              entry.id === item.id
                                ? { ...entry, amount: value }
                                : entry,
                            ),
                          ),
                        )
                      }
                      onChange={() => undefined}
                      placeholder="0.00"
                      type="text"
                      value={item.amount}
                    />
                    <button
                      aria-label="Remove item"
                      {...stylex.props(styles.s34)}
                      disabled={items.length === 1}
                      onClick={() =>
                        setItems((current) =>
                          current.filter((entry) => entry.id !== item.id),
                        )
                      }
                      type="button"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  {...stylex.props(styles.s35)}
                  onClick={() =>
                    setItems((current) => [
                      ...current,
                      { id: Date.now(), name: "", amount: "" },
                    ])
                  }
                  type="button"
                >
                  + Add item
                </button>
              </div>
            ) : (
              splitType !== "equal" && (
                <div {...stylex.props(styles.s30)}>
                  {participantIDs.map((id) => (
                    <label {...stylex.props(styles.s31)} key={id}>
                      <span {...stylex.props(styles.s36)}>
                        {id === data.currentUser.id
                          ? "You"
                          : findPerson(data, id)?.name}
                      </span>
                      <div {...stylex.props(styles.s37)}>
                        <input
                          {...stylex.props(styles.s38)}
                          inputMode="numeric"
                          onKeyDown={(event) =>
                            splitType === "exact"
                              ? handleCentsKeyDown(
                                  event,
                                  splitValues[id] ?? "",
                                  (value) => updateSplitValue(id, value),
                                )
                              : undefined
                          }
                          onChange={(event) =>
                            splitType === "exact"
                              ? undefined
                              : updateSplitValue(id, event.target.value)
                          }
                          placeholder={splitType === "shares" ? "1" : "0"}
                          type="text"
                          value={splitValues[id] ?? ""}
                        />
                        <span {...stylex.props(styles.s39)}>
                          {splitType === "exact"
                            ? currency
                            : splitType === "percentage"
                              ? "%"
                              : "shares"}
                        </span>
                      </div>
                    </label>
                  ))}
                </div>
              )
            )}
            <p {...stylex.props(styles.s27)}>
              {splitType === "equal"
                ? "Split the total evenly."
                : splitType === "exact"
                  ? `Entered ${((splitTotal ?? 0) / 100).toFixed(2)} ${currency} · Remaining ${(((amountCents ?? 0) - splitTotal) / 100).toFixed(2)} ${currency}`
                  : splitType === "percentage"
                    ? `Percent assigned ${((splitTotal ?? 0) / 100).toFixed(2)}% · Remaining ${(100 - (splitTotal ?? 0) / 100).toFixed(2)}%`
                    : splitType === "items"
                      ? `Items ${((itemTotal ?? 0) / 100).toFixed(2)} ${currency} · Remaining ${(((amountCents ?? 0) - itemTotal) / 100).toFixed(2)} ${currency}`
                      : "Each person needs at least one share."}
            </p>
          </fieldset>
        </>

        {error && (
          <p
            {...stylex.props(styles.s40)}
            role="alert"
          >
            {error}
          </p>
        )}
        <div {...stylex.props(styles.s41)}>
          <button
            {...stylex.props(styles.s42)}
            disabled={saving}
            onClick={onClose}
            type="button"
          >
            Cancel
          </button>
          <button
            {...stylex.props(styles.s43)}
            disabled={!isValid || saving}
            type="submit"
          >
            {saving ? "Saving…" : "Save expense"}
            <Icon {...stylex.props(styles.s44)} name="arrow-right" />
          </button>
        </div>
      </form>
    </Dialog>
  );
}
